import { useEffect, useMemo, useState } from "react";
import {
  MagnifyingGlass, CheckCircle, Warning, ShieldCheck, Clock,
  Archive, ArrowSquareOut, NotePencil, Spinner, X,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";

const CHECKLIST_ITEMS = [
  { key: "review_source_checked", label: "Source captured", hint: "Website/source URL captured and confirmed." },
  { key: "review_terms_checked", label: "Terms captured", hint: "Terms URL captured and page reviewed." },
  { key: "review_free_entry_checked", label: "Free entry captured", hint: "Free-entry route found and summary captured." },
  { key: "review_ticket_cap_checked", label: "Ticket cap behaviour reviewed", hint: "Operator typically publishes max ticket caps." },
  { key: "review_winner_visibility_checked", label: "Winner visibility reviewed", hint: "Past winners page or evidence found." },
  { key: "review_extension_policy_checked", label: "Extension policy reviewed", hint: "Extension policy found or noted as unclear." },
  { key: "review_operator_type_checked", label: "Operator type confirmed", hint: "Operator / directory / platform / review site classified." },
  { key: "review_risk_notes_checked", label: "Risk notes reviewed", hint: "Any warning notes captured in review notes." },
];

const OPERATOR_TYPES = [
  { value: "operator", label: "Operator" },
  { value: "directory", label: "Directory" },
  { value: "affiliate_site", label: "Affiliate site" },
  { value: "review_site", label: "Review site" },
  { value: "platform", label: "Platform" },
  { value: "unknown", label: "Unknown" },
];

const STATUS_LABEL = {
  verified: "Verified",
  partial: "Partial",
  seed_candidate: "Seed candidate",
  archived: "Archived",
};

export default function VerificationQueue() {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ count: 0, results: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: "",
    verification_status: "all",
    operator_type: "all",
    priority_tier: "all",
    missing_terms: false,
    missing_free_entry: false,
    missing_source: false,
    missing_trustpilot: false,
    low_confidence: false,
    sort: "priority",
    include_verified: false,
  });
  const [selected, setSelected] = useState(null); // operator being reviewed

  const refresh = async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === false || v === "" || v === "all") return;
      params[k] = v;
    });
    const [q, s] = await Promise.all([
      api.get("/admin/verification-queue", { params }),
      api.get("/admin/verification-stats"),
    ]);
    setData(q.data);
    setStats(s.data);
    // Refresh selected operator if open
    if (selected) {
      const fresh = q.data.results.find((o) => o.id === selected.id);
      if (fresh) setSelected(fresh);
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          <StatPill label="Verified" value={stats.verified} tone="good" />
          <StatPill label="Partial" value={stats.partial} tone="warn" />
          <StatPill label="Seed candidate" value={stats.seed_candidate} tone="neutral" />
          <StatPill label="Archived" value={stats.archived} tone="muted" />
          <StatPill label="Low confidence" value={stats.low_confidence} tone="bad" testId="stat-low-confidence" />
          <StatPill label="Missing terms" value={stats.missing_terms} tone="warn" testId="stat-missing-terms" />
          <StatPill label="Missing free entry" value={stats.missing_free_entry} tone="warn" />
          <StatPill label="Missing Trustpilot" value={stats.missing_trustpilot} tone="muted" />
          <StatPill label="Total operators" value={stats.total} tone="muted" />
        </div>
      )}

      {/* Filters */}
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center bg-white/5 border border-white/15 rounded px-3 flex-1 min-w-[200px] focus-within:border-[var(--cc-gold)]">
            <MagnifyingGlass size={14} className="text-white/40" />
            <input
              value={filters.q}
              onChange={(e) => update("q", e.target.value)}
              placeholder="Search name or domain..."
              data-testid="queue-search"
              className="flex-1 bg-transparent text-white text-sm py-2 px-2 outline-none"
            />
          </div>
          <SelectField value={filters.verification_status} onChange={(v) => update("verification_status", v)} testId="queue-status-filter">
            <option value="all">All non-verified</option>
            <option value="partial">Partial</option>
            <option value="seed_candidate">Seed candidate</option>
            <option value="verified">Verified</option>
            <option value="archived">Archived</option>
          </SelectField>
          <SelectField value={filters.operator_type} onChange={(v) => update("operator_type", v)}>
            <option value="all">All types</option>
            {OPERATOR_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </SelectField>
          <SelectField value={filters.priority_tier} onChange={(v) => update("priority_tier", v)}>
            <option value="all">All tiers</option>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="long_tail">Long tail</option>
          </SelectField>
          <SelectField value={filters.sort} onChange={(v) => update("sort", v)}>
            <option value="priority">Sort: priority</option>
            <option value="verification_status">Sort: status</option>
            <option value="data_confidence">Sort: confidence</option>
            <option value="missing_fields">Sort: missing fields</option>
            <option value="last_verified_at">Sort: last verified</option>
          </SelectField>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            ["missing_terms", "Missing terms URL"],
            ["missing_free_entry", "Missing free-entry URL"],
            ["missing_source", "Missing source URL"],
            ["missing_trustpilot", "Missing Trustpilot"],
            ["low_confidence", "Low confidence"],
            ["include_verified", "Include verified"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => update(k, !filters[k])}
              data-testid={`queue-toggle-${k}`}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                filters[k]
                  ? "bg-[var(--cc-gold)] border-[var(--cc-gold)] text-[var(--cc-navy)]"
                  : "border-white/15 text-white/60 hover:border-white/35"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Queue table */}
      <div className="text-xs text-[var(--cc-text-muted)]">
        {loading ? "Loading..." : `${data.count} operator${data.count === 1 ? "" : "s"} in queue`}
      </div>
      <div className="rounded-lg border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-white/5 text-white/60">
            <tr>
              <th className="text-left px-4 py-3">Operator</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Tier</th>
              <th className="text-left px-4 py-3">URLs</th>
              <th className="text-left px-4 py-3">Checklist</th>
              <th className="text-left px-4 py-3">Confidence</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.results.map((o) => (
              <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`queue-row-${o.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{o.name}</div>
                  <a
                    href={o.source_url || o.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[11px] text-white/40 hover:text-[var(--cc-gold)] inline-flex items-center gap-1"
                  >
                    {(o.source_url || o.website_url || "—").replace(/^https?:\/\//, "")}
                    <ArrowSquareOut size={10} />
                  </a>
                </td>
                <td className="px-4 py-3"><StatusPill status={o.operator_verification_status} /></td>
                <td className="px-4 py-3 text-white/70 text-xs">{o.operator_type || "operator"}</td>
                <td className="px-4 py-3 text-white/70 text-xs">{o.priority_tier || "secondary"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 text-[10px]">
                    <UrlDot has={!!o.terms_url} label="T" hint="Terms URL" />
                    <UrlDot has={!!o.free_entry_url} label="F" hint="Free entry URL" />
                    <UrlDot has={!!o.trustpilot_url} label="TP" hint="Trustpilot URL" />
                    <UrlDot has={!!(o.source_url || o.website_url)} label="S" hint="Source URL" />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-20 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-[var(--cc-gold)]" style={{ width: `${(o.checklist_completed / o.checklist_total) * 100}%` }} />
                    </div>
                    <span className="font-mono-num text-xs text-white/70">{o.checklist_completed}/{o.checklist_total}</span>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono-num text-sm">
                  <span className={
                    (o.data_confidence_score || 0) >= 70 ? "text-emerald-400" :
                    (o.data_confidence_score || 0) >= 50 ? "text-amber-400" : "text-red-400"
                  }>
                    {o.data_confidence_score ?? 0}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setSelected(o)}
                    data-testid={`queue-review-${o.id}`}
                    className="px-3 py-1.5 rounded text-xs bg-white/10 hover:bg-white/20 inline-flex items-center gap-1"
                  >
                    <NotePencil size={12} /> Review
                  </button>
                </td>
              </tr>
            ))}
            {data.results.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-white/40">Queue is clear under these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <ReviewDrawer
          op={selected}
          onClose={() => setSelected(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

const StatPill = ({ label, value, tone, testId }) => {
  const tones = {
    good: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    warn: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    bad: "bg-red-500/10 text-red-300 border-red-500/30",
    neutral: "bg-white/5 text-white/80 border-white/15",
    muted: "bg-white/[0.02] text-white/50 border-white/10",
  }[tone];
  return (
    <div data-testid={testId} className={`rounded-lg p-3 border ${tones}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-mono-num text-xl font-semibold mt-1">{value}</div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const s = status || "seed_candidate";
  const styles = {
    verified: "bg-emerald-500/20 text-emerald-300",
    partial: "bg-amber-500/20 text-amber-300",
    seed_candidate: "bg-slate-500/30 text-slate-200",
    archived: "bg-slate-500/15 text-slate-400",
  }[s];
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${styles}`}>
      {STATUS_LABEL[s]}
    </span>
  );
};

const UrlDot = ({ has, label, hint }) => (
  <span
    title={hint}
    className={`inline-flex items-center justify-center h-5 px-1.5 rounded-full text-[10px] font-semibold border ${
      has
        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
        : "bg-white/5 text-white/30 border-white/10"
    }`}
  >
    {label}
  </span>
);

const SelectField = ({ value, onChange, children, testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="bg-white/5 border border-white/15 rounded px-3 py-2 text-xs text-white"
  >
    {children}
  </select>
);

// ---------------------------------------------------------------
// Review Drawer
// ---------------------------------------------------------------
const ReviewDrawer = ({ op, onClose, onSaved }) => {
  const [form, setForm] = useState({
    ...op,
    terms_url: op.terms_url || "",
    free_entry_url: op.free_entry_url || "",
    trustpilot_url: op.trustpilot_url || "",
    source_url: op.source_url || op.website_url || "",
    manual_review_notes: op.manual_review_notes || "",
    verification_notes: op.verification_notes || "",
    operator_type: op.operator_type || "operator",
    priority_tier: op.priority_tier || "secondary",
    data_confidence_score: op.data_confidence_score ?? 50,
  });
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);

  useEffect(() => {
    // Reset form when op changes
    setForm({
      ...op,
      terms_url: op.terms_url || "",
      free_entry_url: op.free_entry_url || "",
      trustpilot_url: op.trustpilot_url || "",
      source_url: op.source_url || op.website_url || "",
      manual_review_notes: op.manual_review_notes || "",
      verification_notes: op.verification_notes || "",
      operator_type: op.operator_type || "operator",
      priority_tier: op.priority_tier || "secondary",
      data_confidence_score: op.data_confidence_score ?? 50,
    });
  }, [op.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const suggestedConfidence = useMemo(() => {
    const weights = {
      review_source_checked: 10,
      review_terms_checked: 15,
      review_free_entry_checked: 15,
      review_ticket_cap_checked: 15,
      review_winner_visibility_checked: 15,
      review_extension_policy_checked: 10,
      review_operator_type_checked: 10,
      review_risk_notes_checked: 10,
    };
    return Math.min(100, Object.entries(weights).reduce((sum, [k, w]) => sum + (form[k] ? w : 0), 0));
  }, [form]);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = {};
      [
        ...CHECKLIST_ITEMS.map((c) => c.key),
        "terms_url", "free_entry_url", "trustpilot_url", "source_url",
        "operator_type", "priority_tier", "data_confidence_score",
        "manual_review_notes", "verification_notes",
      ].forEach((k) => {
        if (form[k] !== undefined && form[k] !== null && form[k] !== "") payload[k] = form[k];
        else if (typeof form[k] === "boolean") payload[k] = form[k];
      });
      Object.assign(payload, extra);
      await api.post(`/admin/operators/${op.id}/checklist`, payload);
      toast.success("Saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const promote = async (target_status, opts = {}) => {
    setPromoting(true);
    try {
      // Save the form first so the backend sees latest checklist/data_confidence
      await save();
      await api.post(`/admin/operators/${op.id}/promote`, {
        target_status,
        ...opts,
      });
      toast.success(`Marked as ${STATUS_LABEL[target_status]}`);
      onClose();
      onSaved && onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) {
        toast.error(`${detail.message} Missing: ${detail.missing.join(", ")}`);
      } else {
        toast.error("Action failed");
      }
    } finally {
      setPromoting(false);
    }
  };

  const checklistCount = CHECKLIST_ITEMS.filter((c) => form[c.key]).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-end" onClick={onClose}>
      <div
        className="bg-[var(--cc-card-dark)] w-full max-w-2xl h-full overflow-y-auto border-l border-white/10"
        onClick={(e) => e.stopPropagation()}
        data-testid="review-drawer"
      >
        <div className="sticky top-0 z-10 bg-[var(--cc-card-dark)] p-5 border-b border-white/10 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="overline text-[var(--cc-gold)] mb-1">Verification review</div>
            <h2 className="font-display text-xl font-semibold truncate">{op.name}</h2>
            <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
              <StatusPill status={op.operator_verification_status} />
              <span className="px-2 py-0.5 rounded bg-white/10 text-white/70 uppercase tracking-wider font-semibold">
                {op.priority_tier || "secondary"}
              </span>
              {op.operator_type && (
                <span className="px-2 py-0.5 rounded bg-white/10 text-white/70 uppercase tracking-wider font-semibold">
                  {op.operator_type}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1" data-testid="drawer-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Required URLs */}
          <Section title="Captured URLs">
            <UrlField label="Source URL" value={form.source_url} onChange={(v) => set("source_url", v)} required testId="field-source-url" />
            <UrlField label="Terms URL" value={form.terms_url} onChange={(v) => set("terms_url", v)} required testId="field-terms-url" />
            <UrlField label="Free entry URL" value={form.free_entry_url} onChange={(v) => set("free_entry_url", v)} testId="field-free-entry-url" />
            <UrlField label="Trustpilot URL" value={form.trustpilot_url} onChange={(v) => set("trustpilot_url", v)} />
          </Section>

          {/* Operator type + tier */}
          <Section title="Classification">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Operator type">
                <select
                  value={form.operator_type}
                  onChange={(e) => set("operator_type", e.target.value)}
                  data-testid="field-operator-type"
                  className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm"
                >
                  {OPERATOR_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Priority tier">
                <select
                  value={form.priority_tier}
                  onChange={(e) => set("priority_tier", e.target.value)}
                  className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm"
                >
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                  <option value="long_tail">Long tail</option>
                </select>
              </Field>
            </div>
          </Section>

          {/* Checklist */}
          <Section
            title="Review checklist"
            right={<span className="text-xs font-mono-num text-white/60">{checklistCount}/{CHECKLIST_ITEMS.length}</span>}
          >
            <div className="space-y-2">
              {CHECKLIST_ITEMS.map((c) => (
                <label
                  key={c.key}
                  className="flex items-start gap-3 p-2.5 rounded border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!form[c.key]}
                    onChange={(e) => set(c.key, e.target.checked)}
                    data-testid={`checklist-${c.key}`}
                    className="mt-0.5 accent-[var(--cc-gold)]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white">{c.label}</div>
                    <div className="text-[11px] text-white/50">{c.hint}</div>
                  </div>
                  {form[c.key] && <CheckCircle size={14} weight="fill" className="text-emerald-400 mt-0.5" />}
                </label>
              ))}
            </div>
          </Section>

          {/* Confidence */}
          <Section title="Data confidence">
            <div className="rounded-lg bg-white/[0.02] border border-white/10 p-4 flex items-center gap-4">
              <div className="flex-1">
                <label className="overline text-white/50 block mb-1">Current</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.data_confidence_score}
                  onChange={(e) => set("data_confidence_score", Number(e.target.value || 0))}
                  data-testid="field-data-confidence"
                  className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm font-mono-num"
                />
              </div>
              <div className="text-right">
                <div className="overline text-white/50 mb-1">Suggested</div>
                <div className="font-mono-num text-2xl font-semibold text-[var(--cc-gold)]">{suggestedConfidence}</div>
                <button
                  onClick={() => set("data_confidence_score", suggestedConfidence)}
                  data-testid="apply-suggested"
                  className="mt-1 text-[11px] text-[var(--cc-gold)] hover:underline"
                >
                  Use suggested
                </button>
              </div>
            </div>
          </Section>

          {/* Notes */}
          <Section title="Notes">
            <Field label="Manual review notes">
              <textarea
                value={form.manual_review_notes}
                onChange={(e) => set("manual_review_notes", e.target.value)}
                rows={3}
                data-testid="field-review-notes"
                className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm resize-none"
              />
            </Field>
            <Field label="Verification notes">
              <textarea
                value={form.verification_notes}
                onChange={(e) => set("verification_notes", e.target.value)}
                rows={2}
                data-testid="field-verification-notes"
                className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm resize-none"
              />
            </Field>
          </Section>
        </div>

        {/* Actions footer */}
        <div className="sticky bottom-0 bg-[var(--cc-card-dark)] border-t border-white/10 p-4 space-y-2">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => promote("verified")}
              disabled={promoting}
              data-testid="action-promote-verified"
              className="btn-gold px-3 py-2 rounded text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {promoting ? <Spinner size={12} className="animate-spin"/> : <ShieldCheck size={12} weight="fill"/>} Promote to verified
            </button>
            <button
              onClick={() => promote("partial", { force: true })}
              disabled={promoting}
              data-testid="action-mark-partial"
              className="px-3 py-2 rounded text-xs bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 inline-flex items-center gap-1.5"
            >
              <Warning size={12} weight="fill"/> Mark partial
            </button>
            <button
              onClick={() => promote("seed_candidate", { force: true })}
              disabled={promoting}
              className="px-3 py-2 rounded text-xs bg-slate-500/20 text-slate-200 hover:bg-slate-500/30 inline-flex items-center gap-1.5"
            >
              <Clock size={12}/> Seed candidate
            </button>
            <button
              onClick={() => promote("archived", { force: true })}
              disabled={promoting}
              data-testid="action-archive"
              className="px-3 py-2 rounded text-xs bg-red-500/15 text-red-300 hover:bg-red-500/25 inline-flex items-center gap-1.5"
            >
              <Archive size={12}/> Archive
            </button>
          </div>
          <div className="flex justify-between items-center pt-1">
            <button onClick={onClose} className="text-xs text-white/60">Cancel</button>
            <button
              onClick={() => save()}
              disabled={saving}
              data-testid="action-save"
              className="px-4 py-2 rounded text-xs bg-white text-[var(--cc-navy)] font-semibold hover:bg-white/90 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, right, children }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <div className="overline text-white/50">{title}</div>
      {right}
    </div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div className="mb-3">
    <div className="overline text-white/50 mb-1">{label}</div>
    {children}
  </div>
);

const UrlField = ({ label, value, onChange, required, testId }) => (
  <Field label={`${label}${required ? " *" : ""}`}>
    <div className="flex gap-2">
      <input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={required ? "Required for verification" : "Optional"}
        data-testid={testId}
        className={`flex-1 bg-white/5 border rounded px-3 py-2 text-sm outline-none ${
          required && !value ? "border-amber-500/40" : "border-white/15"
        }`}
      />
      {value && (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-2 rounded border border-white/15 text-white/60 hover:text-white"
        >
          <ArrowSquareOut size={14} />
        </a>
      )}
    </div>
  </Field>
);
